/**
 * @fileoverview Base API client configuration
 * 
 * This module provides a configured axios instance with:
 * - Base URL configuration
 * - Request/Response interceptors
 * - Automatic token refresh
 * - Error handling
 * 
 * @module api/client
 */

import axios from 'axios';
import { BASE_URL } from '../../config';
import { toast } from '../utils/toast';
import i18n from '../app/i18n';
import { clearCoGerantChats } from '@/utils/coGerantSession';
import { attachAiPlanGate } from '@/newComponents/ai-plan/aiPlanGate';
import { attachSharedGets } from './sharedGet';

// ============================================================================
// CONFIGURATION
// ============================================================================

// Re-export BASE_URL for use in other modules
export { BASE_URL };

const baseURL = BASE_URL;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Handle unauthorized response by clearing auth data and redirecting to login
 * @param {Object} router - Next.js router instance (optional)
 */
export const handleUnauthorizedResponse = (router) => {
  // On the Support portal, ignore admin-side 401s (e.g. background admin
  // prefetches that carry no admin token). They must NOT bounce the support
  // user to the admin login — the support API client handles its own 401s
  // (→ /support/login). Without this guard, /support/login itself gets ejected.
  if (typeof window !== 'undefined') {
    const p = window.location.pathname;
    if (p === '/support' || p.startsWith('/support/')) return;
    // A dead session fans out into many concurrent 401s (a whole admin page
    // loads ~20 restaurant-scoped calls at once); every one of them lands here.
    // If we're already on the login page, don't fire another redirect — this
    // makes the handler idempotent without any stuck module-level flag.
    if (p === '/admin/login') return;
  }
  localStorage.removeItem('userObj');
  clearCoGerantChats();
  // Guard against callers that pass something other than a router (e.g. a bare
  // restaurant id). A non-router `router` used to throw "e.push is not a
  // function" here and swallow the real 401 — fall back to a hard redirect.
  if (router && typeof router.push === 'function') {
    router.push('/admin/login');
  } else if (typeof window !== 'undefined') {
    window.location.href = '/admin/login';
  }
};

/**
 * Get current user object from localStorage
 * @returns {Object} User object or empty object
 */
export const getCurrentUser = () => {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem('userObj')) ?? {};
  } catch {
    return {};
  }
};

/**
 * Get authorization headers for API requests
 * @returns {Object} Headers object with Authorization
 */
export const getAuthHeaders = () => {
  const user = getCurrentUser();
  return {
    Authorization: `Bearer ${user.access_token}`,
  };
};

/**
 * Get restaurant ID from current user
 * @returns {string|undefined} Restaurant ID
 */
export const getRestaurantId = () => {
  const user = getCurrentUser();
  return user.restaurant_id;
};

// ============================================================================
// AXIOS INSTANCE
// ============================================================================

/**
 * Configured axios instance with interceptors
 */
// ============================================================================
// INTERFACE LANGUAGE
// ============================================================================

const UI_LANGUAGES = ['fr', 'en', 'es', 'de', 'it', 'ar', 'ru'];

/**
 * The language the admin is reading in (i18next), as a bare code — or undefined.
 * The API writes its own texts (insight cards, errors, Co-Gérant answers) in it.
 */
export const uiLanguage = () => {
  let lng = i18n?.language;
  if (!lng && typeof window !== 'undefined') {
    try { lng = localStorage.getItem('i18nextLng'); } catch { lng = null; }
  }
  const code = (lng || '').slice(0, 2).toLowerCase();
  return UI_LANGUAGES.includes(code) ? code : undefined;
};

/** Headers for a raw fetch(): the interface language, set explicitly (never the browser's). */
export const languageHeaders = () => {
  const lng = uiLanguage();
  return lng ? { 'Accept-Language': lng } : {};
};

/**
 * Send the interface language on every request of an axios instance. Set explicitly:
 * the browser's own Accept-Language is the OS language, not the one chosen in the app.
 * @param {import('axios').AxiosInstance} instance
 */
export const attachLanguageHeader = (instance) => {
  instance.interceptors.request.use((config) => {
    const lng = uiLanguage();
    if (lng && config.headers && !config.headers['Accept-Language']) {
      config.headers['Accept-Language'] = lng;
    }
    return config;
  });
  return instance;
};

// Direct axios.get/post calls (api/*.api.js helpers) share the default instance.
attachLanguageHeader(axios);

// AI formulas (owner 11/10): a formula wall on any AI call opens the app-wide modal (aiPlanGate).
attachAiPlanGate(axios);

// The same reference GET asked by several parts of a page at once goes out once (QA 19/10 S5-11).
// Set on the default instance before axios.create below, so apiClient inherits it.
attachSharedGets(axios);

const apiClient = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 90000,
});

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================

/**
 * Matches an interpolated `undefined` / `null` path segment
 * (e.g. `/admin/restaurant/undefined/order/`). Such a request can only 500 on
 * the API — incident 2026-09-20, TraefikHigh5xx — so it is refused client-side.
 */
const UNRESOLVED_SEGMENT = /\/(undefined|null)(\/|$|\?)/;

attachLanguageHeader(apiClient);
attachSharedGets(apiClient);

apiClient.interceptors.request.use(
  (config) => {
    const url = config.url ?? '';
    if (UNRESOLVED_SEGMENT.test(url)) {
      const err = new Error(`Refused API call with unresolved path segment: ${url}`);
      err.code = 'ERR_UNRESOLVED_PATH_SEGMENT';
      err.config = config;
      console.warn(err.message);
      return Promise.reject(err);
    }

    // Auto-add authorization header if user is logged in
    const user = getCurrentUser();
    if (user.access_token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${user.access_token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================

/**
 * Single-flight token refresh.
 *
 * The refresh token rides along as an HttpOnly cookie (withCredentials), so the
 * client never reads it — it just POSTs and lets the browser attach the cookie.
 * `refreshPromise` coalesces concurrent 401s so only ONE /refresh-token/ call is
 * ever in flight, instead of a storm of concurrent refreshes.
 */
let refreshPromise = null;

const doRefresh = async () => {
  const { data } = await apiClient.post('/account/refresh-token/');
  const access_token = data?.access_token;
  if (!access_token) {
    throw new Error('No access_token in refresh response');
  }

  apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

  const userObj = JSON.parse(localStorage.getItem('userObj')) || {};
  userObj.access_token = access_token;
  localStorage.setItem('userObj', JSON.stringify(userObj));

  return access_token;
};

/**
 * Refresh the access token, sharing one in-flight request across all callers.
 * Exported so secondary axios instances (notes, activity-log) reuse the SAME
 * refresh rather than each firing their own.
 * @returns {Promise<string>} the new access token
 */
export const refreshAuthToken = () => {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

/**
 * Attach the standard "401 expired-token -> refresh once -> retry" behavior to
 * any axios instance, so every client stays in sync with one implementation.
 * @param {import('axios').AxiosInstance} instance
 */
export const attachAuthRefreshInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config || {};

      // Never try to refresh the refresh call itself — that would recurse.
      const isRefreshCall =
        originalRequest.url && originalRequest.url.includes('/account/refresh-token/');

      if (
        error.response &&
        error.response.status === 401 &&
        !originalRequest._retry &&
        !isRefreshCall
      ) {
        const errorDetail = error.response.data?.detail;
        const isTokenError =
          errorDetail &&
          (errorDetail.includes('Token has expired') ||
            errorDetail.includes('Could not validate credentials'));

        if (!isTokenError) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        try {
          const access_token = await refreshAuthToken();
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
          return instance(originalRequest);
        } catch (refreshError) {
          handleUnauthorizedResponse();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

// Main client: 401 -> refresh -> retry (shared with the secondary clients).
attachAuthRefreshInterceptor(apiClient);
attachAiPlanGate(apiClient);

// Main client only: 403 -> subscription/trial expired -> redirect to billing.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // AI token wall: the backend returns 402 with a machine-readable body when a
    // restaurant's AI wallet can't cover an operation. Surface a clear top-up
    // prompt (once) without hijacking the user's current page.
    if (error.response && error.response.status === 402) {
      const detail = error.response.data?.detail;
      // A screen that explains the empty wallet itself (the Co-Gérant, with its own
      // « Recharger » button) asks for silence here, so the message is never shown twice.
      if (detail && detail.error === 'insufficient_ai_tokens' && !error.config?.quietAiTokenToast) {
        try {
          // Flat, dot-free key (app i18n uses default keySeparator ".").
          const key = 'Not enough AI tokens for this feature — top up in Settings, AI Tokens';
          toast.error(i18n.t(key, key));
        } catch (_) {
          /* toast/i18n not ready — swallow, the caller still gets the rejection */
        }
      }
    }
    if (error.response && error.response.status === 403) {
      // an object detail (ai_tier_required, …) is not a subscription refusal
      const raw = error.response.data?.detail;
      const detail = typeof raw === 'string' ? raw : '';
      if (
        detail.includes('Trial period has expired') ||
        detail.includes('Subscription inactive')
      ) {
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/subscription')) {
          window.location.href = '/admin/settings/subscription';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// API WRAPPER FUNCTIONS
// ============================================================================

/**
 * Make a GET request with automatic auth
 * @param {string} url - API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} Axios response
 */
export const apiGet = (url, config = {}) => {
  return apiClient.get(url, {
    headers: getAuthHeaders(),
    ...config,
  });
};

/**
 * Make a POST request with automatic auth
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Axios config
 * @returns {Promise} Axios response
 */
export const apiPost = (url, data, config = {}) => {
  return apiClient.post(url, data, {
    headers: getAuthHeaders(),
    ...config,
  });
};

/**
 * Make a PUT request with automatic auth
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Axios config
 * @returns {Promise} Axios response
 */
export const apiPut = (url, data, config = {}) => {
  return apiClient.put(url, data, {
    headers: getAuthHeaders(),
    ...config,
  });
};

/**
 * Make a PATCH request with automatic auth
 * @param {string} url - API endpoint
 * @param {Object} data - Request body
 * @param {Object} config - Axios config
 * @returns {Promise} Axios response
 */
export const apiPatch = (url, data, config = {}) => {
  return apiClient.patch(url, data, {
    headers: getAuthHeaders(),
    ...config,
  });
};

/**
 * Make a DELETE request with automatic auth
 * @param {string} url - API endpoint
 * @param {Object} config - Axios config
 * @returns {Promise} Axios response
 */
export const apiDelete = (url, config = {}) => {
  return apiClient.delete(url, {
    headers: getAuthHeaders(),
    ...config,
  });
};

// Export the base axios instance for advanced use cases
// Also export as axiosInstance for backward compatibility
export { apiClient as axiosInstance };
export default apiClient;
